import type { BaggageFuelEconomyPayload as DashBffBaggageFuelEconomyPayload } from '@makemydeal/dr-dash-bff-types';

/**
 * All Fuel Savings Activity Types
 * Organized by concern: Form types, Component props, Redux store types
 */

/**
 * ============================================================================
 * FORM & INPUT TYPES
 * ============================================================================
 */

/**
 * Period unit for miles driven calculations
 */
export type Period = 'WEEK' | 'MONTH' | 'YEAR';

/**
 * Form input values for fuel saving calculation
 */
export type FuelSavingFormValues = {
    newVehicleMpg: string;
    tradeInMpg: string;
    newVehiclePricePerGallon: string;
    tradePricePerGallon: string;
    milesDriven: string;
    milesPer: Period;
};

/**
 * Working input values with type-safe numbers
 */
export type TradeWorkingInputs = {
    newVehiclePricePerGallon: number | null;
    milesDriven: number | null;
    milesPer: Period;
    tradePricePerGallon: number | null;
    newVehicleMpg: number | null;
    tradeInMpg: number | null;
};

export type TradeWorkingInputField = keyof TradeWorkingInputs;

/**
 * Trade vehicle information
 */
export type Trade = {
    vin: string;
    inputs: TradeWorkingInputs;
};

/**
 * Vehicle information with associated trades
 */
export type Vehicle = {
    vin: string;
    trades: Record<string, Trade>;
};

/**
 * Trade section data for UI display
 */
export type TradeSectionData = {
    id: string;
    name: string;
    vin?: string;
    values: FuelSavingFormValues;
};

/**
 * Vehicle tab for tabbed UI display
 */
export type VehicleTab = {
    id: string;
    label: string;
    combinedFuelEconomy?: number;
    isPrimary?: boolean; // True for primary vehicle, false/undefined for scenario vehicles
};

/**
 * ============================================================================
 * FUEL ECONOMY API TYPES
 * ============================================================================
 */

/**
 * Fuel economy values for a vehicle
 */
export type FuelEconomyValues = {
    highway?: number;
    city?: number;
    combined?: number;
};

/**
 * Fuel economy data for a single vehicle from API
 */
export type VehicleFuelEconomy = {
    vin: string;
    ok: boolean;
    fuelEconomy?: FuelEconomyValues;
    error?: string;
};

/**
 * Fuel economy API response structure
 */
export type FuelEconomyData = {
    ok: boolean;
    message?: string;
    data?: {
        sellingVehiclesVin: VehicleFuelEconomy[];
        tradeinVehiclesVin: VehicleFuelEconomy[];
    };
};

/**
 * Payload for fetching fuel economy data
 */
export type FetchFuelEconomyPayload = {
    sellingVehiclesVin: string[];
    tradeinVehiclesVin: string[];
};

/**
 * ============================================================================
 * VEHICLE & TRADE INFO TYPES (for host app integration)
 * ============================================================================
 */

/**
 * Basic vehicle info from deal (used by host apps to build vehicle tabs)
 */
export type VehicleInfo = {
    vin?: string;
    year?: string | number;
    make?: string;
    model?: string;
    condition?: string;
    combinedFuelEconomy?: number;
};

/**
 * Basic trade-in info (used by host apps to build trade sections)
 */
export type TradeInInfo = {
    year?: string | number;
    make?: string;
    model?: string;
    shouldIncludeTradeIn?: boolean;
    vin?: string;
};

/**
 * ============================================================================
 * COMPONENT PROPS TYPES
 * ============================================================================
 */

/**
 * Trade data in fuel-saving payload
 */
export type FuelSavingApplyTrade = {
    id: string;
    name: string;
    vin?: string;
    values: FuelSavingFormValues;
    savings?: {
        monthly: number;
        yearly: number;
        threeYears: number;
    } | null;
};

/**
 * Payload passed to apply handlers describing the user's current selection.
 * Supports both single-vehicle and multi-vehicle submission formats.
 */
export type FuelSavingApplyPayload = {
    vehicle?: {
        id: string;
        label: string;
        isPrimary?: boolean; // True for primary vehicle, false/undefined for scenario vehicles
    };
    trades?: FuelSavingApplyTrade[];
    allVehicles?: Array<{
        vehicle: {
            id: string;
            label: string;
            isPrimary?: boolean;
        };
        trades: FuelSavingApplyTrade[];
    }>;
};

/**
 * Props for the FuelSavingAnalysis component
 */
export type FuelSavingsProps = {
    isFuelSavingCardState?: boolean;
    isFuelSavingSaving?: boolean;
    isFuelSavingSaveSuccess?: boolean;
    vehicleTabs?: VehicleTab[];
    isVehicleTabsLoading?: boolean;
    trades?: TradeSectionData[];
    fuelEconomyData?: FuelEconomyData | null;
    baggageFuelEconomy?: BaggageFuelEconomyPayload;
    onApply?: (payload: FuelSavingApplyPayload) => void;
    onApplyAndPrint?: (payload: FuelSavingApplyPayload) => void;
    onCancel?: () => void;
    onValuesChange?: (payload: FuelSavingApplyPayload) => void;
};

/**
 * Props for the FuelSavingsResult component
 */
export type FuelSavingsResultProps = {
    monthly: number;
    yearly: number;
    threeYears: number;
};

/**
 * Valid field names in FuelSavingFormValues that exclude the 'milesPer' field.
 * Used for form input field change handlers.
 */
export type FuelSavingInputField = Exclude<keyof FuelSavingFormValues, 'milesPer'>;

export type FieldErrors = Partial<Record<FuelSavingInputField, boolean>>;

export type UseFieldValidationResult = {
    fieldErrors: FieldErrors;
    handleFieldChange: (field: FuelSavingInputField, newValue: string) => void;
};

/**
 * Props for the FuelSavingForm component
 */
export type FuelSavingFormComponentProps = {
    values: FuelSavingFormValues;
    onFieldChange: (field: FuelSavingInputField, value: string) => void;
    onMilesPerChange: (value: Period) => void;
};

/**
 * Params for useFuelSavings hook
 */
export type UseFuelSavingsParams = {
    isFuelSavingCardState: boolean;
    vehicleTabs?: VehicleTab[];
    trades?: TradeSectionData[];
    fuelEconomyData?: FuelEconomyData | null;
    baggageFuelEconomy?: BaggageFuelEconomyPayload;
};

/**
 * ============================================================================
 * REDUX STORE TYPES
 * ============================================================================
 */

/**
 * Redux state for fuel saving activity
 */
export type FuelSavingState = {
    isFuelSavingCardState: boolean;
    formValues: FuelSavingFormValues;
    vehicleTabs: VehicleTab[];
    fuelEconomyData: FuelEconomyData | null;
    isScenarioLoadSettled: boolean;
};

/**
 * Full state tree with fuel saving slice
 */
export type StateTree = {
    [sliceName: string]: FuelSavingState;
};

/**
 * Default trade working inputs
 */
export const defaultTradeWorkingInputs: TradeWorkingInputs = {
    newVehiclePricePerGallon: null,
    milesDriven: null,
    milesPer: 'MONTH',
    tradePricePerGallon: null,
    newVehicleMpg: null,
    tradeInMpg: null
};

/**
 * Initial Redux state for fuel saving activity
 */
export const initialState: FuelSavingState = {
    isFuelSavingCardState: false,
    formValues: {
        newVehicleMpg: '',
        tradeInMpg: '',
        newVehiclePricePerGallon: '',
        tradePricePerGallon: '',
        milesDriven: '',
        milesPer: defaultTradeWorkingInputs.milesPer
    },
    vehicleTabs: [],
    fuelEconomyData: null,
    isScenarioLoadSettled: false
};

/**
 * Baggage structure for fuel savings calculation result
 * Per DEP: trades is a sibling to vehicles at the content level
 */
export type FuelSavingBaggageTrade = {
    fuelPricePerGallon: number;
};

export type FuelSavingBaggageVehicle = {
    fuelPricePerGallon: number;
    milesDrivenPerYear: number;
};

export type FuelSavingBaggageContent = {
    vehicles: Record<string, FuelSavingBaggageVehicle>;
    trades: Record<string, FuelSavingBaggageTrade>;
};

export type FuelSavingBaggage = {
    name: 'deal360.fuelSavings';
    value: {
        type: 'Object';
        content: FuelSavingBaggageContent;
    };
};

export type FuelEconomyTrade = {
    mpg: number;
};

export type FuelEconomyVehicle = {
    mpg: number;
    trades: Record<string, FuelEconomyTrade>;
};

export type FuelEconomyPayload = {
    vehicles: Record<string, FuelEconomyVehicle>;
};

// Alias canonical baggage type to keep this activity in sync with source-of-truth.
export type BaggageFuelEconomyPayload = DashBffBaggageFuelEconomyPayload;
